import IconTime from '@apollo/icons/default/IconTime.svg';
import { Button, ButtonGroup } from '@apollo/orbit';
import { Popover } from '@apollo/space-kit/Popover';
import { Location } from 'history';
import { omit } from 'lodash';
import { ParsedQuery, parse } from 'query-string';
import React, { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { useQueryParam } from 'src/hooks/useQueryParam';
import Config from 'src/lib/config';
import {
  TimeRangeOption,
  printTimeRangeLabel,
} from 'src/lib/config/timeRangeOptions';
import { getAbsoluteDateBounds } from 'src/lib/date/date';
import {
  NormalizedMetricsField,
  NormalizedMetricsFilterField,
} from 'src/lib/metrics';
import { mergeQueryParams } from 'src/lib/routing';

import { TimeRangeFilter } from '../timeRangeFilter/TimeRangeFilter';

import { FilterButton } from './filterButton/FilterButton';
import { FilterPane } from './filterPane/FilterPane';

interface Props {
  selectedRange: TimeRangeOption;
  // This is correctly typed as an Object
  maxRangeInDays: number;
  ranges: ReadonlyArray<TimeRangeOption>;
  resetParams?: Array<keyof MetricsFilterParams>;
}

interface TimeRangeFilterParams {
  [Config.queryParameters.SelectedTimeRange]?: TimeRangeOption;
  [Config.queryParameters.SelectedTimeRangeFrom]?: string;
  [Config.queryParameters.SelectedTimeRangeTo]?: string;
}

interface OperationFilterParams {
  [Config.queryParameters.QueryListMetricSort]?: NormalizedMetricsField;
  [Config.queryParameters.QueryListMetricFilter]?: NormalizedMetricsFilterField;
  [Config.queryParameters.OperationListSearchFilter]?: string;
  [Config.queryParameters.SelectedQueryId]?: string;
  [Config.queryParameters.SelectedQueryName]?: string;
  [Config.queryParameters.FieldListSortDirection]?: string;
  [Config.queryParameters.OperationListSortDirection]?: string;
}

interface ClientFilterParams {
  [Config.queryParameters.ClientFilter]?: string;
}

export interface OperationsPageParams {
  [Config.queryParameters.ActiveReportPaneTab]?: string;
  [Config.queryParameters.SelectedTraceId]?: string;
  [Config.queryParameters.SelectedDurationBucket]?: string;
}

export type MetricsFilterParams = TimeRangeFilterParams &
  OperationFilterParams &
  ClientFilterParams;

interface TraceFilterParams {
  [Config.queryParameters.SelectedTraceId]?: string;
  [Config.queryParameters.SelectedDurationBucket]?: string;
}

export type OperationSearchFilterParams = TimeRangeFilterParams &
  OperationFilterParams &
  ClientFilterParams &
  TraceFilterParams;

export const resetFilterQueryParams: Array<keyof MetricsFilterParams> = [
  Config.queryParameters.OperationListSearchFilter,
  Config.queryParameters.QueryListMetricSort,
  Config.queryParameters.QueryListMetricFilter,
  Config.queryParameters.SelectedTimeRange,
  Config.queryParameters.SelectedTimeRangeFrom,
  Config.queryParameters.SelectedTimeRangeTo,
  Config.queryParameters.ClientFilter,
  Config.queryParameters.SelectedQueryId,
  Config.queryParameters.SelectedQueryName,
  Config.queryParameters.OperationListSortDirection,
];

// Check for anything that's not set to "undefined" in param sources,
// which potentially include query params or location state.
// We could get slightly more sophisticated here with checking for
// equivalence to "default" (i.e. "range=lastDay") but this actually
// felt like a better UX.
export function isResetAllEnabled(
  resetParams: Array<keyof MetricsFilterParams> = resetFilterQueryParams,
  ...paramSources: Array<MetricsFilterParams | ParsedQuery | undefined>
) {
  return paramSources
    .filter((source): source is MetricsFilterParams => source !== undefined)
    .some((source) =>
      Object.keys(source)
        .filter((k): k is keyof MetricsFilterParams =>
          resetParams.includes(k as keyof MetricsFilterParams),
        )
        .some((k) => source[k] !== undefined),
    );
}

export const MetricsFilter = ({
  maxRangeInDays,
  ranges,
  selectedRange,
  resetParams,
}: Props) => {
  const location = useLocation<MetricsFilterParams>();
  const history = useHistory();
  const allQueryParams = useMemo(
    () => parse(location.search),
    [location.search],
  );
  const from = useQueryParam('from');
  const to = useQueryParam('to');

  const setQueryParams = useCallback(
    (newParams: MetricsFilterParams & OperationsPageParams) => {
      history.push({
        ...location,
        search: mergeQueryParams(location.search, newParams),
      });
    },
    [history, location],
  );

  const onRangeSelected = useCallback(
    (range?: TimeRangeOption, fromDate?: string, toDate?: string) =>
      setQueryParams({
        range,
        from: fromDate,
        to: toDate,
      }),
    [setQueryParams],
  );

  const resetAllLocation: Location = {
    ...location,
    search: mergeQueryParams(
      location.search,
      Object.fromEntries(
        (resetParams ?? resetFilterQueryParams).map((key) => [key, undefined]),
      ),
    ),
    state: omit(location.state, resetParams ?? resetFilterQueryParams),
  };
  const resetAllEnabled = isResetAllEnabled(
    resetParams,
    allQueryParams,
    location.state,
  );

  const { timeFrom: absTimeFrom, timeTo: absTimeTo } = getAbsoluteDateBounds(
    selectedRange,
    { from, to },
  );

  return (
    <ButtonGroup>
      <Popover
        interactive
        placement="bottom-end"
        content={
          <FilterPane
            title="Time Range"
            key="Date Filter"
            data-testid="time-range-filter-pane"
          >
            <TimeRangeFilter
              className="w-52"
              onRangeSelected={onRangeSelected}
              ranges={ranges}
              range={selectedRange}
              from={absTimeFrom}
              to={absTimeTo}
              maxRangeInDays={maxRangeInDays}
            />
          </FilterPane>
        }
        trigger={
          <FilterButton variant="hidden" leftIcon={<IconTime />}>
            {printTimeRangeLabel(selectedRange)}
          </FilterButton>
        }
      />
      <Button
        isDisabled={!resetAllEnabled}
        as={Link}
        to={resetAllLocation}
        size="sm"
        variant="secondary"
      >
        Reset
      </Button>
    </ButtonGroup>
  );
};
